import { useEffect, useState } from "react";
import api from "@/lib/api";
import { DataVaultSection, CoachingTreeAdminSection, TeamSection } from "@/pages/MarketingDashboard";
import { Cog, ScrollText, ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";

export default function SystemPage() {
  return (
    <div className="px-4 md:px-8 py-8 max-w-6xl" data-testid="hq-system-page">
      <div className="text-xs tracking-widest text-volt">ADMIN HQ · SYSTEM</div>
      <h1 className="heading text-4xl mt-1">SYSTEM</h1>
      <p className="text-neutral-400 mt-2 text-sm max-w-2xl">
        Technical maintenance — automations, backups, team access, and the audit trail. Nothing here sends outreach.
      </p>

      <AutomationsSection />
      <div id="backups"><DataVaultSection /></div>
      <CoachingTreeAdminSection />
      <div id="team"><TeamSection /></div>
      <div id="audit"><AuditLogSection /></div>
    </div>
  );
}

function AutomationsSection() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/automations").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  return (
    <div className="mt-8 border border-neutral-800 bg-appcard p-5" data-testid="automations-section">
      <div className="text-xs tracking-widest text-volt flex items-center gap-2"><Cog size={13} /> AUTOMATIONS &amp; AGENTS</div>
      <p className="text-[11px] text-neutral-500 mt-1">Every scheduled process, with its last run, next run and any error.</p>
      {!data ? (
        <div className="mt-4 text-neutral-500 text-sm">Loading…</div>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="automations-table">
            <thead>
              <tr className="text-left text-[10px] tracking-widest text-neutral-500 border-b border-neutral-800">
                <th className="py-2 pr-3">PROCESS</th>
                <th className="py-2 pr-3 hidden md:table-cell">LAST RUN</th>
                <th className="py-2 pr-3 hidden md:table-cell">DURATION</th>
                <th className="py-2 pr-3">NEXT RUN</th>
                <th className="py-2">RESULT</th>
              </tr>
            </thead>
            <tbody>
              {data.jobs.map((j) => (
                <tr key={j.id} className="border-b border-neutral-900 align-top" data-testid={`automation-row-${j.id}`}>
                  <td className="py-2.5 pr-3">
                    <div className="text-xs text-white">{j.name}</div>
                    <div className="text-[10px] text-neutral-500">{j.description}</div>
                  </td>
                  <td className="py-2.5 pr-3 hidden md:table-cell text-neutral-400 text-xs whitespace-nowrap">{j.last_run ? new Date(j.last_run).toLocaleString() : "—"}</td>
                  <td className="py-2.5 pr-3 hidden md:table-cell text-neutral-400 text-xs whitespace-nowrap" data-testid={`automation-duration-${j.id}`}>
                    {j.last_duration_ms == null ? "—" : j.last_duration_ms < 1000 ? `${j.last_duration_ms}ms` : `${(j.last_duration_ms / 1000).toFixed(1)}s`}
                  </td>
                  <td className="py-2.5 pr-3 text-neutral-400 text-xs whitespace-nowrap">{j.next_run ? new Date(j.next_run).toLocaleString() : "—"}</td>
                  <td className="py-2.5 text-xs">
                    {j.last_error ? (
                      <span className="text-red-400 inline-flex items-start gap-1"><AlertTriangle size={11} className="mt-0.5 shrink-0" /> {j.last_error.slice(0, 120)}</span>
                    ) : j.last_result && Object.keys(j.last_result).length ? (
                      <span className="text-neutral-400">{Object.entries(j.last_result).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ")}</span>
                    ) : (
                      <span className="text-neutral-600">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AuditLogSection() {
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState(null);
  const limit = 50;
  useEffect(() => {
    api.get(`/admin/audit-log?skip=${skip}&limit=${limit}`).then(({ data }) => setData(data)).catch(() => {});
  }, [skip]);

  return (
    <div className="mt-6 border border-neutral-800 bg-appcard p-5" data-testid="audit-log-section">
      <div className="text-xs tracking-widest text-volt flex items-center gap-2"><ScrollText size={13} /> AUDIT LOG</div>
      <p className="text-[11px] text-neutral-500 mt-1">Who did what: approvals, sends, campaign toggles, password resets, opt-outs, backups.</p>
      {!data ? (
        <div className="mt-4 text-neutral-500 text-sm">Loading…</div>
      ) : data.items.length === 0 ? (
        <div className="mt-4 text-neutral-500 text-sm" data-testid="audit-empty">No audited actions yet.</div>
      ) : (
        <>
          <div className="mt-4 space-y-1" data-testid="audit-list">
            {data.items.map((e, i) => (
              <div key={e.id || i} className="flex items-start gap-3 border-b border-neutral-900 pb-1.5 text-xs" data-testid={`audit-row-${i}`}>
                <span className="text-[10px] text-neutral-600 whitespace-nowrap w-32 shrink-0">{(e.at || "").slice(0, 16).replace("T", " ")}</span>
                <span className="text-volt whitespace-nowrap">{e.actor_email || e.by || "system"}</span>
                <span className="text-neutral-300 min-w-0">
                  {(e.action || "").replace(/[._]/g, " ")}
                  {e.detail ? ` — ${e.detail}` : ""}
                  {e.target_email ? ` → ${e.target_email}` : ""}
                  {e.count != null ? ` (${e.count})` : ""}
                </span>
              </div>
            ))}
          </div>
          {data.total > limit && (
            <div className="mt-3 flex items-center gap-3 text-[11px] text-neutral-500">
              <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} className="border border-neutral-700 px-2.5 py-1.5 disabled:opacity-40"><ChevronLeft size={12} /></button>
              <span>{skip + 1}–{Math.min(skip + limit, data.total)} of {data.total}</span>
              <button disabled={skip + limit >= data.total} onClick={() => setSkip(skip + limit)} className="border border-neutral-700 px-2.5 py-1.5 disabled:opacity-40"><ChevronRight size={12} /></button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
